/**
 * sts-calculator.js
 *
 * Vanilla, dependency-free custom element that powers the STS timber
 * decking calculator (sections/sts-calculator.liquid).
 *
 * Progressive enhancement: the Liquid template renders a fully labelled,
 * pre-filled form from the section settings before this file runs. This
 * script only wires up mode switching, live + on-demand calculation,
 * inline validation and the aria-live results panel. No libraries, no
 * network requests — every calculation happens client-side.
 *
 * Product-metafield prefill: the host element may carry optional
 * data-board-width / data-cover-width / data-lengths attributes, populated
 * by sections/sts-calculator.liquid and sections/sts-product-calculator.liquid
 * (via snippets/sts-calculator-body.liquid) from a product's metafields when
 * one is in scope. See applyHostOverrides() below for exactly how each
 * attribute is used. The host may also carry data-product-title /
 * data-product-url (used by the "Add result to quote" link) and
 * data-pack-info (used by the static "Pack information: …" results line) —
 * see updateQuoteCta() and updatePackInfo().
 *
 * This file also owns the calculator drawer controller (initCalcDrawers(),
 * bottom of this file) that powers sections/sts-product-calculator.liquid's
 * <dialog>. It is deliberately independent of the <sts-calculator> class
 * below — a page may render the drawer, the plain calculator section, or
 * neither, and each piece guards itself against double-initialisation.
 */
(function () {
  'use strict';

  if (customElements.get('sts-calculator')) return;

  class StsCalculator extends HTMLElement {
    /**
     * Pure calculation helpers. Deliberately side-effect free (no DOM
     * access, no `this`) so they can be unit tested or reused elsewhere
     * without instantiating the custom element. All functions return
     * NaN — never throw — when given values that cannot produce a
     * sensible result; callers are responsible for validating inputs
     * before calling these and for never rendering a raw NaN to the UI.
     */
    static calc = {
      /** Cover width in metres from board width + gap, both in mm. */
      coverWidthM(boardWidthMm, gapMm) {
        const width = Number(boardWidthMm);
        const gap = Number(gapMm);
        if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(gap) || gap < 0) return NaN;
        return (width + gap) / 1000;
      },

      /** Linear metres required to cover a given area (m²) at a cover width (m). */
      lmRequired(areaM2, coverWidthM) {
        const area = Number(areaM2);
        const cover = Number(coverWidthM);
        if (!Number.isFinite(area) || area <= 0 || !Number.isFinite(cover) || cover <= 0) return NaN;
        return area / cover;
      },

      /** Linear metres inflated by a wastage percentage. */
      lmWithWastage(lm, wastagePct) {
        const value = Number(lm);
        const wastage = Number(wastagePct);
        if (!Number.isFinite(value) || !Number.isFinite(wastage) || wastage < 0) return NaN;
        return value * (1 + wastage / 100);
      },

      /** Whole boards required (rounded up) to supply at least `lm` at `lengthM` each. */
      boardsRequired(lm, lengthM) {
        const value = Number(lm);
        const length = Number(lengthM);
        if (!Number.isFinite(value) || value <= 0 || !Number.isFinite(length) || length <= 0) return NaN;
        return Math.ceil(value / length);
      },

      /** Total linear metres actually supplied once boards are rounded up. */
      totalLmSupplied(boards, lengthM) {
        const count = Number(boards);
        const length = Number(lengthM);
        if (!Number.isFinite(count) || count < 0 || !Number.isFinite(length) || length <= 0) return NaN;
        return count * length;
      },

      /** Reverse calculation: estimated covered area (m²) from linear metres and cover width. */
      areaFromLm(lm, coverWidthM) {
        const value = Number(lm);
        const cover = Number(coverWidthM);
        if (!Number.isFinite(value) || value <= 0 || !Number.isFinite(cover) || cover <= 0) return NaN;
        return value * cover;
      },
    };

    connectedCallback() {
      // Guard against double-initialisation if the section markup is ever re-rendered in place.
      if (this.dataset.stsInitialized === 'true') return;
      this.dataset.stsInitialized = 'true';

      /** @type {boolean} Whether inline errors should be shown while typing. Flips true after "Calculate". */
      this.showErrors = false;
      /** @type {number|null} Cover width (m) forced by a data-cover-width override, if any. */
      this.coverWidthOverride = null;

      this.modeGroup = this.querySelector('[data-sts-mode-group]');
      this.panels = {
        area: this.querySelector('[data-sts-panel="area"]'),
        linear: this.querySelector('[data-sts-panel="linear"]'),
      };
      this.calculateBtn = this.querySelector('[data-sts-calculate]');
      this.resetBtn = this.querySelector('[data-sts-reset]');
      this.resultsEmptyEl = this.querySelector('[data-sts-results-empty]');
      this.resultList = this.querySelector('[data-sts-result-list]');
      this.packInfoEl = this.querySelector('[data-sts-pack-info]');
      this.quoteCtaEl = this.querySelector('[data-sts-quote-cta]');

      if (!this.modeGroup || !this.panels.area || !this.panels.linear || !this.calculateBtn || !this.resetBtn) {
        return; // Markup incomplete — fail quietly rather than throw.
      }

      this.applyHostOverrides();
      this.captureFieldDefaults();

      this.modeGroup.addEventListener('change', (event) => {
        if (event.target instanceof HTMLInputElement && event.target.matches('[data-sts-mode-radio]')) {
          this.onModeChange();
        }
      });

      this.addEventListener('input', (event) => {
        if (event.target instanceof HTMLElement && event.target.matches('[data-sts-input]')) {
          this.onFieldChange(event);
        }
      });

      this.addEventListener('change', (event) => {
        if (event.target instanceof HTMLSelectElement && event.target.matches('[data-sts-input]')) {
          this.onFieldChange(event);
        }
      });

      this.calculateBtn.addEventListener('click', () => {
        this.showErrors = true;
        this.calculate();
      });

      this.resetBtn.addEventListener('click', () => this.reset());

      this.bindVariantTracking();

      this.syncPanelVisibility();
      this.calculate(); // Quiet initial pass — required fields are blank, so this just shows the empty prompt.
    }

    /**
     * Reads optional data-board-width / data-cover-width / data-lengths attributes on the host
     * element and, when present, lets them take priority over the section-setting defaults
     * already baked into the rendered inputs. This is the hook a future product-metafield
     * prefill can use without any markup or script changes:
     *  - data-board-width: overwrites both mode panels' "Board width (mm)" input value.
     *  - data-cover-width: a pre-known cover width (m) that is used directly in calculations
     *    instead of deriving it from board width + gap, until the shopper edits either of
     *    those two fields (at which point we fall back to computing it, since they've taken
     *    manual control).
     *  - data-lengths: a comma-separated list of board lengths (m) that replaces the "Board
     *    length" select's options (still keeping the "No set length" first option).
     */
    applyHostOverrides() {
      const boardWidthAttr = (this.getAttribute('data-board-width') || '').trim();
      const coverWidthAttr = (this.getAttribute('data-cover-width') || '').trim();
      const lengthsAttr = (this.getAttribute('data-lengths') || '').trim();

      if (boardWidthAttr !== '' && Number.isFinite(Number(boardWidthAttr))) {
        this.querySelectorAll('[data-sts-input="board_width"]').forEach((el) => {
          if (el instanceof HTMLInputElement) {
            el.value = boardWidthAttr;
            return;
          }
          // Phase C2: board width is a <select> of governed catalogue widths.
          // Only adopt the product's own nominal width when it is genuinely
          // one of those options — assigning an unlisted value would silently
          // blank the control and leave the shopper with no width at all.
          if (el instanceof HTMLSelectElement) {
            const wanted = String(Number(boardWidthAttr));
            const match = Array.from(el.options).find((option) => String(Number(option.value)) === wanted);
            if (match) el.value = match.value;
          }
        });
      }

      if (coverWidthAttr !== '' && Number.isFinite(Number(coverWidthAttr)) && Number(coverWidthAttr) > 0) {
        // The sts.coverage_width metafield is stored in millimetres; no real
        // cover width in metres exceeds ~0.3, so any value above 3 is mm.
        let coverWidth = Number(coverWidthAttr);
        if (coverWidth > 3) coverWidth = coverWidth / 1000;
        this.coverWidthOverride = coverWidth;
      }

      if (lengthsAttr !== '') {
        const lengths = lengthsAttr
          .split(',')
          .map((value) => value.trim())
          .filter((value) => /^\d+(\.\d+)?$/.test(value));

        if (lengths.length) {
          const select = this.querySelector('[data-sts-input="length"]');
          if (select instanceof HTMLSelectElement) {
            const options = ['<option value="">No set length</option>'];
            lengths.forEach((value) => {
              options.push(`<option value="${value}">${value} m</option>`);
            });
            select.innerHTML = options.join('');
          }
        }
      }
    }

    /** Snapshots each field's current (post-override) value so Reset has something to restore. */
    captureFieldDefaults() {
      this.fieldDefaults = new Map();
      this.querySelectorAll('[data-sts-input]').forEach((el) => {
        if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) {
          this.fieldDefaults.set(el, el.value);
        }
      });
    }

    /** @returns {'area'|'linear'} */
    getActiveMode() {
      const checked = this.querySelector('[data-sts-mode-radio]:checked');
      return checked instanceof HTMLInputElement && checked.value === 'linear' ? 'linear' : 'area';
    }

    syncPanelVisibility() {
      const active = this.getActiveMode();
      Object.entries(this.panels).forEach(([mode, panel]) => {
        if (!panel) return;
        const isActive = mode === active;
        panel.hidden = !isActive;
        if (isActive) {
          panel.removeAttribute('aria-hidden');
        } else {
          panel.setAttribute('aria-hidden', 'true');
        }
      });
    }

    onModeChange() {
      this.showErrors = false;
      this.syncPanelVisibility();
      this.clearAllErrors();
      this.calculate();
    }

    onFieldChange(event) {
      const target = event.target;
      const name = target instanceof HTMLElement ? target.dataset.stsInput : null;
      if ((name === 'board_width' || name === 'gap') && this.coverWidthOverride !== null) {
        // The shopper is now editing the raw board dimensions directly — stop forcing
        // the metafield-derived cover width and compute it from their inputs instead.
        this.coverWidthOverride = null;
      }
      this.calculate();
    }

    reset() {
      this.showErrors = false;
      this.coverWidthOverride = null; // A fresh Reset returns fully to the section/metafield defaults.
      this.applyHostOverrides();
      this.fieldDefaults.forEach((value, el) => {
        el.value = value;
      });

      const radios = this.querySelectorAll('[data-sts-mode-radio]');
      radios.forEach((radio, index) => {
        if (radio instanceof HTMLInputElement) radio.checked = index === 0;
      });

      this.syncPanelVisibility();
      this.clearAllErrors();
      this.calculate();
    }

    /** Parses a raw input string into a finite number, or NaN for blank/invalid input. */
    parseNum(raw) {
      if (raw === null || raw === undefined) return NaN;
      const trimmed = String(raw).trim();
      if (trimmed === '') return NaN;
      const value = Number(trimmed);
      return Number.isFinite(value) ? value : NaN;
    }

    /** Formats a number to `dp` decimal places, or an em dash if it is not finite. */
    formatNum(value, dp) {
      return Number.isFinite(value) ? value.toFixed(dp) : 'n/a';
    }

    /**
     * Validates the required + range-bound fields for the given mode.
     * @returns {{el: Element, message: string}[]} Empty when the panel is calculation-ready.
     */
    validate(mode, panel) {
      const errors = [];
      const getInput = (name) => panel.querySelector(`[data-sts-input="${name}"]`);

      const primaryEl = getInput(mode === 'area' ? 'area' : 'lm');
      const primaryVal = this.parseNum(primaryEl && primaryEl.value);
      if (!Number.isFinite(primaryVal) || primaryVal <= 0) {
        errors.push({
          el: primaryEl,
          message: mode === 'area' ? 'Enter a deck area greater than 0.' : 'Enter linear metres greater than 0.',
        });
      }

      const boardWidthEl = getInput('board_width');
      const boardWidthVal = this.parseNum(boardWidthEl && boardWidthEl.value);
      if (!Number.isFinite(boardWidthVal) || boardWidthVal <= 0) {
        errors.push({ el: boardWidthEl, message: 'Enter a board width greater than 0.' });
      }

      const gapEl = getInput('gap');
      const gapRaw = gapEl ? gapEl.value.trim() : '';
      if (gapRaw !== '') {
        const gapVal = this.parseNum(gapRaw);
        if (!Number.isFinite(gapVal) || gapVal < 0) {
          errors.push({ el: gapEl, message: 'Gap cannot be negative.' });
        }
      } else if (mode === 'area' && this.isGapRequired()) {
        // No verified gap for this product: never fall back to 0 or a default.
        // LM -> area still prices the entered LM; only its area waits for a gap.
        errors.push({ el: gapEl, message: 'Enter the gap specified by your installer or supplier.' });
      }

      if (mode === 'area') {
        const wastageEl = getInput('wastage');
        const wastageRaw = wastageEl ? wastageEl.value.trim() : '';
        if (wastageRaw !== '') {
          const wastageVal = this.parseNum(wastageRaw);
          if (!Number.isFinite(wastageVal) || wastageVal < 0 || wastageVal > 100) {
            errors.push({ el: wastageEl, message: 'Wastage must be between 0 and 100.' });
          }
        }
      }

      return errors;
    }

    /** True when the host product has no verified board gap (data-gap-required). */
    isGapRequired() {
      return this.getAttribute('data-gap-required') === 'true';
    }

    clearAllErrors() {
      this.querySelectorAll('.sts-calculator__error').forEach((el) => {
        el.hidden = true;
        el.textContent = '';
      });
      this.querySelectorAll('[data-sts-input]').forEach((el) => el.removeAttribute('aria-invalid'));
    }

    renderErrors(errors) {
      this.clearAllErrors();
      errors.forEach(({ el, message }) => {
        if (!el) return;
        const describedBy = el.getAttribute('aria-describedby');
        const errorEl = describedBy ? document.getElementById(describedBy) : null;
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
        }
        el.setAttribute('aria-invalid', 'true');
      });
    }

    showEmptyResults(message) {
      if (this.resultList) {
        this.resultList.hidden = true;
        this.resultList.innerHTML = '';
      }
      if (this.resultsEmptyEl) {
        this.resultsEmptyEl.hidden = false;
        this.resultsEmptyEl.textContent =
          message || 'Enter your measurements and select Calculate to see an estimate.';
      }
      this.updatePackInfo('');
      this.updateQuoteCta(null);
    }

    /**
     * Static, non-computed pack-size line shown under the results once a
     * board count exists — never invented, only ever the merchant/metafield
     * copy carried on the host's data-pack-info attribute.
     */
    updatePackInfo(value) {
      if (!this.packInfoEl) return;
      const text = (value || '').trim();
      if (text !== '') {
        this.packInfoEl.textContent = `Pack information: ${text}`;
        this.packInfoEl.hidden = false;
      } else {
        this.packInfoEl.hidden = true;
        this.packInfoEl.textContent = '';
      }
    }

    /**
     * "Add result to quote" link + "Use This Quantity" button, rendered
     * only once a valid calculation exists. The link goes to the
     * request-a-quote page with the product (from the host's
     * data-product-title attribute, if present), a human-readable quantity
     * summary, the selected board length and the board count (when those
     * apply) carried as query-string params for
     * assets/sts-quote-prefill.js to pick up. See buildUseQuantityButton()
     * for the second action.
     */
    updateQuoteCta(meta) {
      if (!this.quoteCtaEl) return;
      this.quoteCtaEl.innerHTML = '';
      if (!meta || !meta.quantity) return;

      const params = new URLSearchParams();
      const productTitle = (this.getAttribute('data-product-title') || '').trim();
      if (productTitle !== '') params.set('product', productTitle);
      if (meta.quantity) params.set('quantity', meta.quantity);
      if (meta.boardLength) params.set('board_length', meta.boardLength);
      if (meta.boards) params.set('boards', meta.boards);

      const href = `/pages/request-a-quote?${params.toString()}`;
      const link = document.createElement('a');
      link.className = 'sts-btn sts-btn--secondary';
      link.href = href;
      link.textContent = 'Add result to quote';
      this.quoteCtaEl.appendChild(link);

      const useQuantityButton = this.buildUseQuantityButton(meta);
      if (useQuantityButton) this.quoteCtaEl.appendChild(useQuantityButton);
    }

    /** @returns {'lm'|'boards'|''} The host's data-purchase-model, normalised. */
    getPurchaseModel() {
      const raw = (this.getAttribute('data-purchase-model') || '').trim().toLowerCase();
      return raw === 'boards' || raw === 'lm' ? raw : '';
    }

    /**
     * @returns {string} The variant id the shopper currently has selected. Cached from the
     * variant control's own data-variant-id (the product form updates its hidden id field
     * asynchronously), falling back to that hidden field on first paint.
     */
    getSelectedVariantId() {
      if (this.selectedVariantId) return this.selectedVariantId;
      const idInput = document.querySelector('product-form-component input[name="id"]');
      return idInput instanceof HTMLInputElement ? idInput.value : '';
    }

    /**
     * @returns {number} The SELECTED variant's per-LM price, or NaN when it cannot be resolved.
     *
     * Only meaningful for "lm" products: on a "boards" product the variant price is the price
     * of a whole board, not a rate per linear metre, so it must never be used here.
     * sts.price_per_lm is a single product-level value, so on a product whose variants are
     * priced differently (Grey Ironbark 14.90 vs Red 14.80) it understates the selected variant.
     */
    getSelectedVariantLmPrice() {
      if (this.getPurchaseModel() !== 'lm') return NaN;
      const variantId = this.getSelectedVariantId();
      if (!variantId) return NaN;
      // The calculator is its own section, so the product form is not a descendant of it.
      for (const scope of [this.closest('.shopify-section, dialog'), document]) {
        if (!scope) continue;
        const script = scope.querySelector('[data-sts-variants]');
        if (!script || !script.textContent) continue;
        try {
          const entry = JSON.parse(script.textContent)[variantId];
          if (entry && Number.isFinite(entry.price)) return entry.price;
        } catch (error) {
          /* malformed payload — fall through to the metafield */
        }
      }
      return NaN;
    }

    /**
     * @returns {number} The rate the estimated-cost row prices against.
     * Authority order: selected variant price, then the host's data-lm-rate
     * (sts.price_per_lm) as fallback. NaN when neither is available — the cost
     * row is then skipped entirely rather than invented.
     */
    getLmRate() {
      const raw = this.getAttribute('data-lm-rate');
      const fallback = raw && raw.trim() !== '' ? this.parseNum(raw) : NaN;
      const selected = this.getSelectedVariantLmPrice();
      return Number.isFinite(selected) && selected > 0 ? selected : fallback;
    }

    /**
     * Keeps the estimated-cost row in step with the variant picker. Without this the row
     * would keep pricing against whichever variant was selected when the page loaded.
     */
    bindVariantTracking() {
      if (this.getPurchaseModel() !== 'lm') return;
      const idInput = document.querySelector('product-form-component input[name="id"]');
      if (idInput instanceof HTMLInputElement) this.selectedVariantId = idInput.value;

      document.addEventListener('change', (event) => {
        const target = event.target;
        let variantId = null;
        if (target instanceof HTMLInputElement && target.matches('input[type="radio"][data-variant-id]')) {
          variantId = target.getAttribute('data-variant-id');
        } else if (target instanceof HTMLSelectElement) {
          const selected = target.selectedOptions && target.selectedOptions[0];
          if (selected instanceof HTMLElement) variantId = selected.getAttribute('data-variant-id');
        }
        if (!variantId || variantId === this.selectedVariantId) return;
        this.selectedVariantId = variantId;
        this.calculate();
      });
    }

    /**
     * Appends an "Estimated cost" row to the results list. Kept separate
     * from renderResults() because its value is a full formula sentence
     * ("4.8 LM × $45.60/LM = $218.88 inc. GST"), not a single number+unit —
     * and because it only ever appears when the host carries a real
     * data-lm-rate (sts.price_per_lm). Never invents a rate.
     *
     * `lmValue` is the quantity actually priced. When it was rounded up from
     * the calculated figure (`calculatedLm`), the row says so, so the sentence
     * always multiplies out exactly as shown.
     */
    appendCostRow(lmValue, rate, cost, calculatedLm) {
      if (!this.resultList) return;
      const shownLm = Number.isInteger(lmValue) ? String(lmValue) : this.formatNum(lmValue, 1);
      const rounded =
        Number.isFinite(calculatedLm) && Math.abs(calculatedLm - lmValue) > 1e-6
          ? ` (rounded up from ${this.formatNum(calculatedLm, 1)} LM)`
          : '';
      const row = document.createElement('div');
      row.className = 'sts-calculator__result-row sts-calculator__result-row--cost';
      row.innerHTML = `
        <dt class="sts-calculator__result-label">
          Estimated cost
          <span class="sts-chip--neutral">Estimate only. Confirm at cart</span>
        </dt>
        <dd class="sts-calculator__result-value sts-tabular">
          ${shownLm} LM &times; $${rate.toFixed(2)}/LM = $${cost.toFixed(2)} inc. GST${rounded}
        </dd>`;
      this.resultList.appendChild(row);
    }

    /**
     * Pack-opening fee note under the cost row. Only when the host carries a
     * full-pack quantity (data-pack-lm) and a fee (data-pack-fee, $ inc. GST),
     * and only when the quantity that would go to the cart is below the pack.
     * The fee is stated, never added to the estimated cost.
     */
    appendPackFeeNote(orderLm) {
      if (!this.resultList) return;
      const packLm = this.parseNum(this.getAttribute('data-pack-lm'));
      const feeInc = this.parseNum(this.getAttribute('data-pack-fee'));
      if (!Number.isFinite(packLm) || packLm <= 0 || !Number.isFinite(feeInc) || feeInc <= 0) return;
      if (!Number.isFinite(orderLm) || orderLm <= 0 || orderLm >= packLm) return;
      const row = document.createElement('div');
      row.className = 'sts-calculator__result-row sts-calculator__result-row--fee';
      row.innerHTML = `
        <dt class="sts-calculator__result-label">
          Pack-opening fee
          <span class="sts-chip--neutral">Not included in this estimate or the online checkout total</span>
        </dt>
        <dd class="sts-calculator__result-value sts-tabular">
          Orders below ${packLm} LM incur a pack-opening fee of $${feeInc.toFixed(2)} including GST per eligible product. We&rsquo;ll contact you to confirm it before arranging payment.
        </dd>`;
      this.resultList.appendChild(row);
    }

    /**
     * "Use This Quantity" button — only rendered when the host declares a
     * recognised data-purchase-model AND this calculation produced a usable
     * quantity for that model (a board count for "boards" products, an LM
     * figure for "lm" products). Never rendered otherwise — there would be
     * nothing sensible to push into the buy box.
     */
    buildUseQuantityButton(meta) {
      const model = this.getPurchaseModel();
      if (model === 'boards') {
        if (!Number.isFinite(meta.boardsNum) || meta.boardsNum <= 0) return null;
      } else if (model === 'lm') {
        if (!Number.isFinite(meta.lmNum) || meta.lmNum <= 0) return null;
      } else {
        return null;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sts-btn sts-btn--primary sts-use-quantity-btn';
      button.textContent = 'Use This Quantity';
      button.addEventListener('click', () => this.applyQuantityToBuyBox(model, meta));
      return button;
    }

    /**
     * Pushes the calculator's result into the on-page buy box: sets the
     * quantity input (ceil(LM) for "lm" products, the exact board count for
     * "boards" products) and, for "boards" products, selects the variant
     * radio whose length matches the calculated board length when one
     * exists. Then closes this calculator's drawer (if inside one) and
     * scrolls the purchase summary into view. Degrades silently when the
     * buy box isn't on the page or its markup doesn't match expectations —
     * never throws.
     */
    applyQuantityToBuyBox(model, meta) {
      const qtyInput = document.querySelector('product-form-component input[name="quantity"]');
      if (!(qtyInput instanceof HTMLInputElement)) return;

      let targetQty = NaN;

      if (model === 'boards') {
        targetQty = meta.boardsNum;
        if (Number.isFinite(meta.lengthNum)) {
          document.querySelectorAll('input[type="radio"][data-variant-id]').forEach((radio) => {
            if (!(radio instanceof HTMLInputElement) || radio.checked) return;
            const label = radio.closest('label');
            const text = label && label.textContent ? label.textContent.trim() : '';
            const leading = parseFloat(text);
            if (Number.isFinite(leading) && Math.abs(leading - meta.lengthNum) < 0.05) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }
      } else {
        targetQty = this.ceilLm(meta.lmNum);
      }

      const clamped = this.clampToQtyInput(targetQty, qtyInput);
      if (!Number.isFinite(clamped)) return;

      qtyInput.value = String(clamped);
      qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      qtyInput.dispatchEvent(new Event('blur', { bubbles: true }));

      this.closeHostDrawer();
      this.scrollToBuyBoxAndFlash();
    }

    /**
     * Snaps a target quantity onto what the buy box's quantity input accepts
     * (its min, max and step; 1/none/1 when absent). NaN for a non-positive or
     * non-finite target. Shared by "Use This Quantity" and the estimated-cost
     * row so the priced quantity is always the quantity that reaches the cart.
     */
    clampToQtyInput(targetQty, qtyInput) {
      if (!Number.isFinite(targetQty) || targetQty <= 0) return NaN;
      const input = qtyInput instanceof HTMLInputElement ? qtyInput : null;
      const min = input ? this.parseNum(input.min) : NaN;
      const max = input ? this.parseNum(input.max) : NaN;
      const step = input ? this.parseNum(input.step) : NaN;
      const effectiveMin = Number.isFinite(min) ? min : 1;
      const effectiveStep = Number.isFinite(step) && step > 0 ? step : 1;

      let clamped = Math.max(effectiveMin, targetQty);
      if (Number.isFinite(max)) clamped = Math.min(max, clamped);
      const stepsAbove = Math.ceil((clamped - effectiveMin) / effectiveStep);
      clamped = effectiveMin + stepsAbove * effectiveStep;
      if (Number.isFinite(max) && clamped > max) clamped = max;
      return clamped;
    }

    /**
     * The linear metres an "lm" shopper can actually order for a calculated
     * LM figure: rounded up, then snapped to the quantity input exactly as
     * applyQuantityToBuyBox() does.
     */
    orderableLm(lm) {
      const qtyInput = document.querySelector('product-form-component input[name="quantity"]');
      return this.clampToQtyInput(this.ceilLm(lm), qtyInput);
    }

    /**
     * Rounds an LM figure up to a whole metre. Settled to 6 dp first so float
     * noise cannot add a metre: 50 LM + 10 % is 55.00000000000001 in JS and
     * must order 55, not 56.
     */
    ceilLm(lm) {
      return Number.isFinite(lm) ? Math.ceil(Number(lm.toFixed(6))) : NaN;
    }

    /** Closes this calculator instance's own <dialog> drawer, if it is inside one. */
    closeHostDrawer() {
      const dialog = this.closest('dialog');
      if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
    }

    /** Scrolls the on-page purchase summary (or the buy box) into view and flashes it. */
    scrollToBuyBoxAndFlash() {
      const summary = document.querySelector('[data-sts-summary]');
      const target = summary || document.querySelector('product-form-component');
      if (!target) return;

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });

      if (summary) {
        summary.classList.add('sts-purchase-summary--flash');
        window.setTimeout(() => summary.classList.remove('sts-purchase-summary--flash'), 1200);
      }
    }

    renderResults(rows) {
      if (!this.resultList || !this.resultsEmptyEl) return;

      // Never render a raw NaN — every row value has already been through formatNum().
      const markup = rows
        .map(
          (row) => `
        <div class="sts-calculator__result-row">
          <dt class="sts-calculator__result-label">
            Estimated ${row.label}
            <span class="sts-chip--neutral">Estimate</span>
          </dt>
          <dd class="sts-calculator__result-value sts-tabular">
            ${row.value}<span class="sts-calculator__result-unit"> ${row.unit}</span>
          </dd>
        </div>`
        )
        .join('');

      this.resultList.innerHTML = markup;
      this.resultList.hidden = false;
      this.resultsEmptyEl.hidden = true;
    }

    /**
     * Shows each panel's guide warning (data-sts-gap-warning) while its gap is below the
     * guide minimum in data-gap-min. A blank gap counts as below: it is read as 0.
     */
    syncGapWarnings() {
      Object.values(this.panels || {}).forEach((panel) => {
        if (!panel || typeof panel.querySelectorAll !== 'function') return;
        const gapEl = panel.querySelector('[data-sts-input="gap"]');
        panel.querySelectorAll('[data-sts-gap-warning]').forEach((warning) => {
          const min = Number(warning.getAttribute('data-gap-min'));
          const gap = gapEl ? this.parseNum(gapEl.value) : NaN;
          warning.hidden = !(Number.isFinite(min) && min > 0 && !(gap >= min));
        });
      });
    }

    calculate() {
      const mode = this.getActiveMode();
      const panel = this.panels[mode];
      if (!panel) return;

      this.syncGapWarnings();
      const errors = this.validate(mode, panel);

      if (errors.length) {
        if (this.showErrors) {
          this.renderErrors(errors);
          this.showEmptyResults('Check the highlighted fields above.');
        } else {
          this.showEmptyResults();
        }
        return;
      }

      this.clearAllErrors();

      const getInput = (name) => panel.querySelector(`[data-sts-input="${name}"]`);
      const calc = this.constructor.calc;

      const boardWidth = this.parseNum(getInput('board_width').value);
      const gapEl = getInput('gap');
      const gap = gapEl && gapEl.value.trim() !== '' ? this.parseNum(gapEl.value) : 0;

      const coverWidthM = Number.isFinite(this.coverWidthOverride)
        ? this.coverWidthOverride
        : calc.coverWidthM(boardWidth, gap);

      if (mode === 'area') {
        const area = this.parseNum(getInput('area').value);
        const wastageEl = getInput('wastage');
        const wastage = wastageEl && wastageEl.value.trim() !== '' ? this.parseNum(wastageEl.value) : 0;
        const lengthEl = getInput('length');
        const lengthVal = lengthEl && lengthEl.value.trim() !== '' ? this.parseNum(lengthEl.value) : NaN;

        const lm = calc.lmRequired(area, coverWidthM);
        const lmWithWastage = calc.lmWithWastage(lm, wastage);

        const rows = [
          { label: 'linear metres', value: this.formatNum(lm, 1), unit: 'm' },
          { label: 'linear metres with wastage', value: this.formatNum(lmWithWastage, 1), unit: 'm' },
        ];

        let boardsForMeta = '';
        let lengthForMeta = '';
        let packInfoValue = '';
        let boardsNum = NaN;
        let lengthNum = NaN;
        let totalLmSupplied = NaN;

        if (Number.isFinite(lengthVal) && lengthVal > 0 && Number.isFinite(lmWithWastage)) {
          const boards = calc.boardsRequired(lmWithWastage, lengthVal);
          const totalLm = calc.totalLmSupplied(boards, lengthVal);
          rows.push({ label: 'board count', value: this.formatNum(boards, 0), unit: 'boards' });
          rows.push({ label: 'total linear metres supplied', value: this.formatNum(totalLm, 1), unit: 'm' });
          boardsForMeta = this.formatNum(boards, 0);
          lengthForMeta = this.formatNum(lengthVal, 1);
          packInfoValue = this.getAttribute('data-pack-info') || '';
          boardsNum = boards;
          lengthNum = lengthVal;
          totalLmSupplied = totalLm;
        }

        this.renderResults(rows);
        this.updatePackInfo(packInfoValue);

        // Estimated cost — only ever shown when the host carries a real LM rate.
        // On an "lm" product it prices the quantity "Use This Quantity" sends to
        // the cart (whole LM), not the unrounded figure shown above it.
        const lmRate = this.getLmRate();
        let costBasisLm = lmWithWastage;
        if (Number.isFinite(totalLmSupplied)) costBasisLm = totalLmSupplied;
        else if (this.getPurchaseModel() === 'lm') costBasisLm = this.orderableLm(lmWithWastage);
        if (Number.isFinite(lmRate) && lmRate > 0 && Number.isFinite(costBasisLm) && costBasisLm > 0) {
          this.appendCostRow(costBasisLm, lmRate, costBasisLm * lmRate, lmWithWastage);
        }
        if (this.getPurchaseModel() === 'lm') this.appendPackFeeNote(costBasisLm);

        this.updateQuoteCta({
          quantity: Number.isFinite(lmWithWastage) ? `${this.formatNum(lmWithWastage, 1)} LM (incl. waste)` : '',
          boardLength: lengthForMeta,
          boards: boardsForMeta,
          lmNum: Number.isFinite(lmWithWastage) ? lmWithWastage : NaN,
          boardsNum,
          lengthNum,
        });
      } else {
        const lm = this.parseNum(getInput('lm').value);
        const gapMissing = this.isGapRequired() && (!gapEl || gapEl.value.trim() === '');
        const area = gapMissing ? NaN : calc.areaFromLm(lm, coverWidthM);

        this.renderResults([
          gapMissing
            ? { label: 'coverage area', value: 'Enter a board gap to estimate', unit: '' }
            : { label: 'coverage area', value: this.formatNum(area, 2), unit: 'm²' },
        ]);
        this.updatePackInfo('');

        // Estimated cost — the shopper entered LM directly in this mode, so
        // no wastage allowance applies; only shown when a real rate exists.
        const lmRate = this.getLmRate();
        const costLm = this.getPurchaseModel() === 'lm' ? this.orderableLm(lm) : lm;
        if (Number.isFinite(lmRate) && lmRate > 0 && Number.isFinite(costLm) && costLm > 0) {
          this.appendCostRow(costLm, lmRate, costLm * lmRate, lm);
        }
        if (this.getPurchaseModel() === 'lm') this.appendPackFeeNote(costLm);

        this.updateQuoteCta({
          quantity: Number.isFinite(area) ? `${this.formatNum(area, 2)} m² (estimated coverage)` : '',
          boardLength: '',
          boards: '',
          lmNum: Number.isFinite(lm) ? lm : NaN,
          boardsNum: NaN,
          lengthNum: NaN,
        });
      }
    }
  }

  customElements.define('sts-calculator', StsCalculator);

  /**
   * Calculator drawer controller — powers sections/sts-product-calculator.liquid's
   * <dialog class="sts-calc-drawer" data-sts-calc-drawer>. Deliberately
   * independent of the StsCalculator class above: a page may render the
   * drawer, the plain calculator section, both, or neither, and each
   * <sts-calculator> inside a drawer still behaves exactly as it does on
   * the standalone calculator page. Uses the native <dialog> element for
   * modal behaviour, backdrop and ESC handling — no focus-trap code needed.
   */
  /** @type {WeakMap<HTMLDialogElement, HTMLElement>} Remembers which trigger opened which drawer, for focus return. */
  const calcDrawerOpeners = new WeakMap();

  function openCalcDrawer(dialog, trigger) {
    calcDrawerOpeners.set(dialog, trigger);
    dialog.dataset.stsPrevOverflow = document.body.style.overflow || '';
    document.body.style.overflow = 'hidden';
    dialog.showModal();
  }

  function initCalcDrawers(root) {
    const scope = root || document;

    scope.querySelectorAll('[data-sts-calc-open]').forEach((trigger) => {
      if (trigger.dataset.stsCalcOpenBound === 'true') return;
      trigger.dataset.stsCalcOpenBound = 'true';

      trigger.addEventListener('click', () => {
        // Prefer a drawer inside the trigger's own section (the calculator
        // section's inline trigger row), then fall back to the first drawer
        // anywhere on the page — this is how the buy-box trigger rendered by
        // blocks/buy-buttons.liquid (inside the product-information section)
        // reaches the drawer hosted by sections/sts-product-calculator.liquid.
        const section = trigger.closest('[id^="shopify-section-"]') || document;
        let dialog = section.querySelector('[data-sts-calc-drawer]');
        if (!(dialog instanceof HTMLDialogElement)) {
          dialog = document.querySelector('[data-sts-calc-drawer]');
        }
        if (dialog instanceof HTMLDialogElement) {
          openCalcDrawer(dialog, trigger);
        }
      });
    });

    // Buy-box calculator trigger (blocks/buy-buttons.liquid) is rendered with
    // the `hidden` attribute and only revealed once we can confirm a drawer
    // host actually exists on the page — if the calculator section is removed,
    // the trigger simply never appears (no dead button).
    if (document.querySelector('[data-sts-calc-drawer]')) {
      document.querySelectorAll('[data-sts-calc-buybox][hidden]').forEach((wrapper) => {
        wrapper.removeAttribute('hidden');
      });
    }

    scope.querySelectorAll('[data-sts-calc-drawer]').forEach((dialog) => {
      if (!(dialog instanceof HTMLDialogElement) || dialog.dataset.stsCalcDrawerBound === 'true') return;
      dialog.dataset.stsCalcDrawerBound = 'true';

      const closeBtn = dialog.querySelector('[data-sts-calc-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => dialog.close());
      }

      // Backdrop click: a click that lands on ::backdrop fires with the
      // dialog itself as the event target (no descendant was clicked).
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });

      // Native ESC already triggers the dialog's 'cancel' -> 'close' sequence.
      dialog.addEventListener('close', () => {
        document.body.style.overflow = dialog.dataset.stsPrevOverflow || '';
        const opener = calcDrawerOpeners.get(dialog);
        if (opener && typeof opener.focus === 'function') opener.focus();
        calcDrawerOpeners.delete(dialog);
      });
    });
  }

  /**
   * Live purchase summary controller — powers blocks/buy-buttons.liquid's
   * .sts-purchase-summary panel (Selected length / Boards-or-Linear metres
   * / Total LM / Subtotal), kept in sync as the shopper changes variant or
   * quantity. Deliberately independent of the StsCalculator class above —
   * it requires no <sts-calculator> on the page at all, and each summary
   * instance guards itself against double-initialisation.
   *
   * Variant detection: a delegated `change` listener for the variant
   * radios/selects rendered by snippets/variant-main-picker.liquid, read
   * via their existing `data-variant-id` attribute (radios) or
   * `selectedOptions[0].dataset.variantId` (dropdowns). This is an
   * optimistic, synchronous read with no network round trip — correct here
   * because the "boards" purchase model only ever varies by a single
   * "Length" option, so one radio/option always maps directly to one
   * variant. (Horizon's own variant swap is an async section fetch with no
   * plain-script-safe completion event, so this file — loaded as a classic,
   * non-module <script> — reads the option's own data-variant-id instead of
   * awaiting that fetch.)
   *
   * Quantity detection: native `input`/`change` on the quantity
   * <input name="quantity">, plus the theme's own 'quantity-selector:update'
   * event (ThemeEvents.quantitySelectorUpdate in assets/events.js — fired by
   * assets/component-quantity-selector.js on +/- click and on blur-snap) for
   * the authoritative, clamped value.
   */
  function initPurchaseSummary(root) {
    const scope = root || document;

    scope.querySelectorAll('[data-sts-summary]').forEach((summaryEl) => {
      if (summaryEl.dataset.stsSummaryBound === 'true') return;
      summaryEl.dataset.stsSummaryBound = 'true';

      const container = summaryEl.closest('.shopify-section, dialog') || document;
      const model = summaryEl.dataset.model === 'boards' ? 'boards' : 'lm';
      const lmRateAttr = summaryEl.getAttribute('data-lm-rate');
      const lmRate = lmRateAttr ? Number(lmRateAttr) : NaN;

      const variantsScript = summaryEl.querySelector('[data-sts-variants]');
      let variantsMap = {};
      if (variantsScript && variantsScript.textContent) {
        try {
          const parsed = JSON.parse(variantsScript.textContent);
          if (parsed && typeof parsed === 'object') variantsMap = parsed;
        } catch (error) {
          variantsMap = {};
        }
      }

      const lengthRowEl = summaryEl.querySelector('[data-sts-selected-length]');
      const qtyLabelEl = summaryEl.querySelector('[data-sts-qty-label]');
      const qtyValueEl = summaryEl.querySelector('[data-sts-qty-value]');
      const totalLmEl = summaryEl.querySelector('[data-sts-total-lm]');
      const subtotalEl = summaryEl.querySelector('[data-sts-subtotal]');

      // Seeded from the hidden variant-id form field so the very first
      // update() (before any shopper interaction) matches the server render.
      const initialIdInput = container.querySelector('product-form-component input[name="id"]');
      let currentVariantId =
        initialIdInput instanceof HTMLInputElement ? initialIdInput.value : '';

      function currentQty() {
        const qtyInput = container.querySelector('product-form-component input[name="quantity"]');
        const value = qtyInput instanceof HTMLInputElement ? Number(qtyInput.value) : NaN;
        return Number.isFinite(value) && value > 0 ? value : 1;
      }

      function formatLm(value) {
        return Number.isFinite(value) ? `${value.toFixed(1)} m` : 'n/a';
      }

      function formatMoney(value) {
        return Number.isFinite(value) ? `$${value.toFixed(2)} inc. GST` : 'n/a';
      }

      function update() {
        const qty = currentQty();
        const variantMeta = variantsMap[currentVariantId];

        let totalLm = NaN;
        let subtotal = NaN;

        if (model === 'boards') {
          const lengthM = variantMeta && Number.isFinite(variantMeta.length) ? variantMeta.length : NaN;
          const unitPrice = variantMeta && Number.isFinite(variantMeta.price) ? variantMeta.price : NaN;
          totalLm = Number.isFinite(lengthM) ? lengthM * qty : NaN;
          subtotal = Number.isFinite(unitPrice) ? unitPrice * qty : NaN;

          if (qtyLabelEl) qtyLabelEl.textContent = 'Boards';
          if (lengthRowEl) lengthRowEl.textContent = formatLm(lengthM);
        } else {
          totalLm = qty;
          // The selected variant's own price is the authority. sts.price_per_lm is a
          // single product-level value, so on a product whose variants are priced
          // differently (Grey vs Red Ironbark) it understates the selected variant.
          // Fall back to the metafield only when the variant price is unavailable.
          const lmUnitPrice =
            variantMeta && Number.isFinite(variantMeta.price) ? variantMeta.price : lmRate;
          subtotal = Number.isFinite(lmUnitPrice) ? lmUnitPrice * qty : NaN;
          if (qtyLabelEl) qtyLabelEl.textContent = 'Linear metres';
        }

        if (qtyValueEl) qtyValueEl.textContent = Number.isFinite(qty) ? String(qty) : 'n/a';
        if (totalLmEl) totalLmEl.textContent = formatLm(totalLm);
        if (subtotalEl) subtotalEl.textContent = formatMoney(subtotal);
      }

      container.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;

        if (target instanceof HTMLInputElement && target.matches('input[type="radio"][data-variant-id]')) {
          currentVariantId = target.getAttribute('data-variant-id') || currentVariantId;
          update();
        } else if (target instanceof HTMLSelectElement) {
          const selected = target.selectedOptions && target.selectedOptions[0];
          const variantId = selected instanceof HTMLElement ? selected.getAttribute('data-variant-id') : null;
          if (variantId) {
            currentVariantId = variantId;
            update();
          }
        } else if (target instanceof HTMLInputElement && target.matches('input[name="quantity"]')) {
          update();
        }
      });

      container.addEventListener('input', (event) => {
        const target = event.target;
        if (target instanceof HTMLInputElement && target.matches('input[name="quantity"]')) {
          update();
        }
      });

      // Authoritative, clamped quantity after +/- click or blur-snap.
      container.addEventListener('quantity-selector:update', () => update());

      update();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initCalcDrawers();
      initPurchaseSummary();
    });
  } else {
    initCalcDrawers();
    initPurchaseSummary();
  }

  // Re-bind newly injected drawers/triggers/summaries after a Theme Editor section reload.
  document.addEventListener('shopify:section:load', (event) => {
    initCalcDrawers(event.target);
    initPurchaseSummary(event.target);
  });
})();
